// src/App.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Star,
  StarOff,
  ChevronDown,
  ChevronRight,
  Check,
  HelpCircle,
  BarChart3,
  LayoutDashboard,
  LogOut,
} from "lucide-react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./auth/authConfig";
import FleetStats from "./FleetStats";
import FleetOverview from "./FleetOverview";

console.log("React version:", React?.version);

// Set to "" to disable the domain gate while testing
const ALLOWED_DOMAIN = "";

/* -------- wheelme brand palette -------- */
const C = {
  blueBlack: "#021c23",
  darkBlue: "#04252e",
  deepBlue: "#073440",
  strongBlue: "#09465f",
  grey3: "#395d66",
  grey2: "#9caeb3",
  grey1: "#cdd6d9",
  offWhite: "#efefef",
  neonGreen: "#7bff89",
};

const PAGE_BG = `linear-gradient(180deg, ${C.blueBlack} 0%, ${C.darkBlue} 100%)`;
const CARD_BG = "rgba(255,255,255,0.025)";
const CARD_BORDER = "1px solid rgba(255,255,255,0.06)";
const SUBTLE_BORDER = "rgba(205,214,217,0.28)";

/* -------- shared button styles --------
   These mirror the FleetStats page so both files feel like one app. */
function brandButton({ active = false } = {}) {
  return {
    backgroundColor: active ? C.strongBlue : "transparent",
    color: C.offWhite,
    border: `1px solid ${SUBTLE_BORDER}`,
    transition: "all 0.15s ease",
  };
}

function hoverNeon(e) {
  e.currentTarget.style.borderColor = C.neonGreen;
  e.currentTarget.style.color = C.neonGreen;
}
function unhoverNeon(e, active = false) {
  e.currentTarget.style.borderColor = SUBTLE_BORDER;
  e.currentTarget.style.color = C.offWhite;
}

/* ---------- Small UI helpers ---------- */
function useOnClickOutside(ref, handler) {
  useEffect(() => {
    function listener(e) {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    }
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

function CheckboxRow({ label, checked, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded transition-colors"
      style={{ color: checked ? C.offWhite : C.grey1 }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
    >
      <span
        className="inline-flex items-center justify-center h-4 w-4 rounded-sm"
        style={{
          backgroundColor: checked ? C.neonGreen : "transparent",
          color: checked ? C.blueBlack : "transparent",
          border: `1px solid ${checked ? C.neonGreen : C.grey3}`,
        }}
      >
        {checked ? <Check size={12} /> : null}
      </span>
      <span className="text-sm">{label}</span>
    </button>
  );
}

/* Version multi-select dropdown */
function VersionMultiSelect({
  options = [],
  selectedSet,
  setSelectedSet,
  allSelected,
  setAllSelected,
}) {
  const [open, setOpen] = useState(false);
  const boxRef = React.useRef(null);
  useOnClickOutside(boxRef, () => setOpen(false));

  const display = useMemo(() => {
    if (allSelected) return "All";
    if (selectedSet.size === 0) return "Select version";
    if (selectedSet.size <= 2) return Array.from(selectedSet).join(", ");
    return `${selectedSet.size} selected`;
  }, [selectedSet, allSelected]);

  const toggle = (opt) => {
    const next = new Set(selectedSet);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    setAllSelected(false);
    setSelectedSet(next);
  };

  const chooseAll = () => {
    setSelectedSet(new Set());
    setAllSelected(true);
    setOpen(false);
  };

  const clear = () => {
    setSelectedSet(new Set());
    setAllSelected(false);
  };

  return (
    <div
      ref={boxRef}
      className="relative inline-flex w-full items-stretch rounded-xl"
      style={{ border: `1px solid rgba(205,214,217,0.18)` }}
    >
      <span
        className="px-3 py-2 text-sm border-r select-none whitespace-nowrap rounded-l-xl"
        style={{
          backgroundColor: C.darkBlue,
          color: C.grey1,
          borderColor: "rgba(205,214,217,0.18)",
        }}
      >
        Version
      </span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex-1 text-left p-2 pr-8 rounded-r-xl"
        style={{ backgroundColor: "#020b16", color: C.offWhite }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {display}
      </button>
      <ChevronDown
        size={16}
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
        style={{ color: C.grey2 }}
      />

      {open && (
        <div
          className="absolute z-40 left-0 right-0 top-full mt-1 rounded-xl p-2 shadow-lg"
          style={{
            backgroundColor: C.deepBlue,
            border: `1px solid ${C.grey3}`,
          }}
        >
          <div className="max-h-64 overflow-auto">
            <button
              type="button"
              onClick={chooseAll}
              className="w-full text-left text-sm px-2 py-1.5 rounded transition-colors"
              style={{ color: C.offWhite }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              All
            </button>
            <div className="h-px my-1" style={{ backgroundColor: C.grey3 }} />
            {options.map((opt) => (
              <CheckboxRow
                key={opt}
                label={opt}
                checked={selectedSet.has(opt)}
                onToggle={() => toggle(opt)}
              />
            ))}
          </div>

          <div className="mt-2 flex justify-between gap-2">
            <button
              className="text-xs px-2 py-1 rounded-lg transition-colors"
              style={brandButton()}
              onMouseEnter={hoverNeon}
              onMouseLeave={(e) => unhoverNeon(e)}
              onClick={clear}
            >
              Clear
            </button>
            <button
              className="text-xs px-2 py-1 rounded-lg transition-colors"
              style={brandButton()}
              onMouseEnter={hoverNeon}
              onMouseLeave={(e) => unhoverNeon(e)}
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Top-level with MSAL wrapper ---------------- */
export default function SiteDirectory() {
  const { instance } = useMsal();
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const active = instance.getActiveAccount?.();
  const userEmail =
    active?.username || active?.idTokenClaims?.preferred_username || "";

  useEffect(() => {
    const cbId = instance.addEventCallback((event) => {
      if (event.eventType === "msal:loginSuccess" && event.payload?.account) {
        instance.setActiveAccount(event.payload.account);
        setIsAuthenticated(true);
      }
    });
    return () => cbId && instance.removeEventCallback(cbId);
  }, [instance]);

  useEffect(() => {
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
    setAuthChecked(true);
  }, [instance]);

  const signIn = () => instance.loginRedirect(loginRequest);
  const signOut = () =>
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });

  useEffect(() => {
    if (
      authChecked &&
      isAuthenticated &&
      ALLOWED_DOMAIN &&
      userEmail &&
      !userEmail.toLowerCase().endsWith(ALLOWED_DOMAIN.toLowerCase())
    ) {
      alert("Work accounts only.");
      signOut();
    }
  }, [authChecked, isAuthenticated, userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!authChecked) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: PAGE_BG, color: C.offWhite }}
      >
        Checking login…
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: PAGE_BG, color: C.offWhite }}
      >
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Wheelme Portal</h1>
          <p className="mb-6" style={{ color: C.grey1 }}>
            Sign in with your work account
          </p>
          <button
            onClick={signIn}
            className="px-5 py-3 rounded-xl text-sm font-semibold transition-colors"
            style={brandButton()}
            onMouseEnter={hoverNeon}
            onMouseLeave={(e) => unhoverNeon(e)}
          >
            Sign in with Microsoft
          </button>
        </div>
      </div>
    );
  }

  return <DirectoryApp userEmail={userEmail} onSignOut={signOut} />;
}

function readBool(v, fallback = false) {
  if (v == null) return fallback;
  return v === "1" || v === "true";
}

function updateUrlParams(patch) {
  const url = new URL(window.location.href);

  Object.entries(patch).forEach(([key, value]) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === false
    ) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, String(value));
    }
  });

  window.history.replaceState({}, "", url.toString());
}
/* ---------------- Authenticated App ---------------- */
function DirectoryApp({ userEmail, onSignOut }) {
  const initialUrl = new URL(window.location.href);

  const [siteData, setSiteData] = useState(null);

  const [search, setSearch] = useState(initialUrl.searchParams.get("search") || "");
  const [favorites, setFavorites] = useState([]);
  const [recents, setRecents] = useState([]);
  const [view, setView] = useState(initialUrl.searchParams.get("view") || "all");
  const [expanded, setExpanded] = useState({});
  const [showFleetStats, setShowFleetStats] = useState(
    readBool(initialUrl.searchParams.get("fleet"), false)
  );
  const [showFleetOverview, setShowFleetOverview] = useState(
    readBool(initialUrl.searchParams.get("overview"), false)
  );

  const [regionFilter, setRegionFilter] = useState(
    initialUrl.searchParams.get("region") || ""
  );

  const [versionSet, setVersionSet] = useState(new Set());
  const [versionAllSelected, setVersionAllSelected] = useState(false);

  const [showOtherRecents, setShowOtherRecents] = useState(false);
  const [showHowTo, setShowHowTo] = useState(
    readBool(initialUrl.searchParams.get("help"), false)
  );

  useEffect(() => {
    const load = async () => {
      try {
        const [sites, versions] = await Promise.all([
          fetch("/sites.json", { cache: "no-store" }).then((r) => r.json()),
          fetch("/versions.json", { cache: "no-store" })
            .then((r) => (r.ok ? r.json() : {}))
            .catch(() => ({})),
        ]);

        const mergeVersions = (obj) => {
          if (!obj) return {};
          const out = {};
          for (const [org, groups] of Object.entries(obj)) {
            out[org] = {};
            for (const [group, arr] of Object.entries(groups)) {
              out[org][group] = arr.map((s) => ({
                ...s,
                softwareVersion: s.softwareVersion ?? versions?.[s.id] ?? null,
              }));
            }
          }
          return out;
        };

        setSiteData({
          internal: mergeVersions(sites.internal),
          external: mergeVersions(sites.external || {}),
        });
      } catch (err) {
        console.error("Failed to load site data", err);
      }
    };
    load();
  }, []);

  useEffect(() => {
    updateUrlParams({
      search,
      view: view !== "all" ? view : null,
      fleet: showFleetStats ? 1 : null,
      help: showHowTo ? 1 : null,
      region: regionFilter || null,
      versions: !versionAllSelected && versionSet.size > 0
        ? Array.from(versionSet).join(",")
        : null,
      versionsAll: versionAllSelected ? 1 : null,
    });
  }, [search, view, showFleetStats, showHowTo, regionFilter, versionSet, versionAllSelected]);

  useEffect(() => {
    const storedFavs = JSON.parse(localStorage.getItem("favorites")) || [];
    const storedRecents = JSON.parse(localStorage.getItem("recents")) || [];
    setFavorites(storedFavs);
    setRecents(storedRecents);
  }, []);

  const handleVisit = (site, targetUrl) => {
    const updated = [site, ...recents.filter((s) => s.name !== site.name)];
    const trimmed = updated.slice(0, 3);
    setRecents(trimmed);
    localStorage.setItem("recents", JSON.stringify(trimmed));
    window.open(targetUrl, "_blank");
  };

  const toggleFavorite = (site) => {
    let updated;
    if (favorites.find((f) => f.name === site.name)) {
      updated = favorites.filter((f) => f.name !== site.name);
    } else {
      updated = [...favorites, site];
    }
    setFavorites(updated);
    localStorage.setItem("favorites", JSON.stringify(updated));
  };

  const toggleOrg = (org) =>
    setExpanded((prev) => ({ ...prev, [org]: !prev[org] }));

  const allVersions = useMemo(() => {
    if (!siteData) return [];
    const collect = (obj) =>
      Object.values(obj || {}).flatMap((groups) =>
        Object.values(groups || {})
          .flat()
          .map((s) => s.softwareVersion)
          .filter(Boolean)
      );
    const versions = [
      ...new Set([
        ...collect(siteData.internal),
        ...collect(siteData.external),
      ]),
    ];
    return versions.sort();
  }, [siteData]);

  const filterActive =
    (regionFilter && regionFilter !== "All") ||
    (!versionAllSelected && versionSet.size > 0);

  const matchesSearch = (s) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;

    return [
      s.name,
      s.client,
      s.clientLocation,
      s.region,
      s.org,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(q));
  };

  const normalizeRegion = (r) => {
    const x = (r || "").toLowerCase();
    if (x.startsWith("eu")) return "europe";
    if (x.startsWith("us")) return "us";
    if (x.startsWith("ap") || x.startsWith("me") || x.startsWith("af")) return "asia";
    return x;
  };

  const matchesRegionVersion = (s) => {
    const matchesRegion =
      regionFilter === "" ||
      regionFilter === "All" ||
      normalizeRegion(s.region) === normalizeRegion(regionFilter);

    const matchesVersion =
      versionAllSelected ||
      versionSet.size === 0 ||
      versionSet.has(s.softwareVersion || "");

    return matchesRegion && matchesVersion;
  };

  const filterAndFlatten = (dataObj) =>
    Object.entries(dataObj || {}).reduce((acc, [org, groups]) => {
      const sites = Object.entries(groups || {}).flatMap(([_, groupSites]) =>
        groupSites.map((site) => ({ ...site, org }))
      );

      const filtered = sites.filter(
        (s) => matchesSearch(s) && matchesRegionVersion(s)
      );

      if (filtered.length > 0) acc[org] = filtered;
      return acc;
    }, {});

  const recentsBySearch = useMemo(
    () => recents.filter((s) => matchesSearch(s)),
    [recents, search]
  );
  const recentMatching = useMemo(
    () => recentsBySearch.filter((s) => matchesRegionVersion(s)),
    [recentsBySearch, regionFilter, versionSet, versionAllSelected]
  );
  const recentOther = useMemo(
    () => recentsBySearch.filter((s) => !matchesRegionVersion(s)),
    [recentsBySearch, regionFilter, versionSet, versionAllSelected]
  );

  const SiteCard = ({ site, outsideBadge = false }) => {
    const subline = `${site.region || ""}${
      site.softwareVersion ? ` • ${site.softwareVersion}` : ""
    }`;

    const openDashboard = (e) => {
      e.stopPropagation();
      handleVisit(site, site.url);
    };

    const openApp = (e) => {
      e.stopPropagation();
      const appUrl = site.appUrl || site.url?.split("/grafana")[0] || site.url;
      handleVisit(site, appUrl);
    };

    return (
      <motion.div
        key={`${site.org}-${site.name}`}
        whileHover={{ scale: 1.005 }}
        className="cursor-default"
      >
        <div
          className="group rounded-xl p-3 flex items-center gap-4 transition-colors"
          style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.14)")}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)")}
        >
          <MapPin size={18} style={{ color: C.neonGreen, flexShrink: 0 }} />

          <div className="min-w-0 pr-4">
            <div className="flex items-center gap-2">
              <div
                className="text-[15px] font-semibold capitalize truncate"
                style={{ color: C.offWhite }}
              >
                {site.name}
              </div>
              {outsideBadge && (
                <span
                  className="text-[10px] leading-none px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: C.deepBlue, color: C.grey1 }}
                >
                  outside filters
                </span>
              )}
            </div>
            <div className="text-xs truncate" style={{ color: C.grey2 }}>
              {subline}
            </div>
          </div>

          <div
            className="flex items-center gap-2 h-8 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={openDashboard}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
              style={brandButton()}
              onMouseEnter={hoverNeon}
              onMouseLeave={(e) => unhoverNeon(e)}
            >
              Insights
            </button>

            <button
              onClick={openApp}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
              style={brandButton()}
              onMouseEnter={hoverNeon}
              onMouseLeave={(e) => unhoverNeon(e)}
            >
              App
            </button>

            <div
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite(site);
              }}
              className="cursor-pointer pl-1"
              title="Toggle favorite"
            >
              {favorites.find((f) => f.name === site.name) ? (
                <Star size={16} style={{ color: C.neonGreen }} />
              ) : (
                <StarOff size={16} style={{ color: C.grey2 }} />
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderSites = (orgs) =>
    Object.entries(orgs || {}).map(([org, sites]) => (
      <div key={org} className="mb-6">
        <button
          onClick={() => toggleOrg(org)}
          className="flex items-center gap-2 text-sm font-semibold mb-3"
          style={{ color: C.grey2 }}
        >
          {expanded[org] ? (
            <ChevronDown size={16} />
          ) : (
            <ChevronRight size={16} />
          )}
          <span className="uppercase tracking-wide">{org}</span>
        </button>
        {expanded[org] && (
          <div className="flex flex-col gap-3">
            {sites.map((site) => (
              <SiteCard key={`${org}-${site.name}`} site={site} />
            ))}
          </div>
        )}
      </div>
    ));

  if (!siteData) {
    return (
      <div
        className="min-h-screen px-6 py-10"
        style={{ background: PAGE_BG, color: C.offWhite }}
      >
        Loading site data…
      </div>
    );
  }

  if (showFleetOverview) {
    return (
      <FleetOverview
        userEmail={userEmail}
        onSignOut={onSignOut}
        onBack={() => setShowFleetOverview(false)}
      />
    );
  }

  if (showFleetStats) {
    return (
      <FleetStats
        userEmail={userEmail}
        onSignOut={onSignOut}
        onBack={() => setShowFleetStats(false)}
      />
    );
  }

  return (
    <div
      className="min-h-screen px-6 py-10"
      style={{ background: PAGE_BG, color: C.offWhite }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1
              className="text-3xl font-bold"
              style={{ color: C.offWhite }}
            >
              Wheelme Portal
            </h1>

            <button
              onClick={() => setShowHowTo((v) => !v)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors"
              style={brandButton()}
              onMouseEnter={hoverNeon}
              onMouseLeave={(e) => unhoverNeon(e)}
              title="How to use this portal"
            >
              <HelpCircle size={16} />
              {showHowTo ? "Hide guide" : "How to use"}
            </button>
          </div>

          <p style={{ color: C.grey2 }}>Find the site</p>
        </div>

        {/* Top-right actions */}
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => setShowFleetStats(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors"
            style={brandButton()}
            onMouseEnter={hoverNeon}
            onMouseLeave={(e) => unhoverNeon(e)}
            title="Fleet stats dashboard"
          >
            <BarChart3 size={16} />
            Fleet stats
          </button>

          <button
            onClick={() => setShowFleetOverview(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors"
            style={brandButton()}
            onMouseEnter={hoverNeon}
            onMouseLeave={(e) => unhoverNeon(e)}
            title="Fleet overview dashboard"
          >
            <LayoutDashboard size={16} />
            Fleet overview
          </button>

          <span style={{ color: C.grey2 }} className="px-2">
            {userEmail}
          </span>

          <button
            onClick={onSignOut}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors"
            style={brandButton()}
            onMouseEnter={hoverNeon}
            onMouseLeave={(e) => unhoverNeon(e)}
            title="Sign out"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </div>

      {/* How to use panel */}
      {showHowTo && (
        <div
          className="mb-6 rounded-xl p-4 text-sm"
          style={{
            backgroundColor: CARD_BG,
            border: CARD_BORDER,
            color: C.grey1,
          }}
        >
          <h2
            className="text-base font-semibold mb-2"
            style={{ color: C.offWhite }}
          >
            How to use Wheelme Portal
          </h2>

          <p className="mb-3" style={{ color: C.grey1 }}>
            This page collects all Wheelme cloud sites in one place, so it's easy to
            jump straight to the Insights dashboards for any site in the list.
          </p>

          <ul className="list-disc pl-5 space-y-1">
            <li>
              <span style={{ color: C.offWhite }}>Search:</span> Use the search bar to
              filter sites by name.
            </li>
            <li>
              <span style={{ color: C.offWhite }}>Location filter:</span> Pick a region
              (or choose All) to narrow the list.
            </li>
            <li>
              <span style={{ color: C.offWhite }}>Version filter:</span> Select one or
              more software versions, or choose All to disable version filtering.
            </li>
            <li>
              <span style={{ color: C.offWhite }}>Recent:</span> Your last 3 opened
              sites show up in Recent (search always applies).
            </li>
            <li>
              <span style={{ color: C.offWhite }}>Favorites:</span> Click the star to
              save a site. Switch to the Favorites tab to see them.
            </li>
            <li>
              <span style={{ color: C.offWhite }}>Fleet stats:</span> Click the Fleet stats
              button to see driving distance and time for all robots across all sites.
            </li>
          </ul>

          <div className="mt-3" style={{ color: C.grey2 }}>
            Tip: Filters affect both Internal and External lists. Recent shows
            matching items first, but you can expand "outside filters".
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-xl mb-4">
        <input
          type="text"
          placeholder="Search locations"
          className="w-full p-3 pl-10 rounded-xl focus:outline-none"
          style={{
            backgroundColor: "#020b16",
            color: C.offWhite,
            border: `1px solid rgba(205,214,217,0.18)`,
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <MapPin
          size={18}
          className="absolute top-3.5 left-3"
          style={{ color: C.grey2 }}
        />
      </div>

      {/* Filters row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 max-w-2xl">
        {/* Location */}
        <div
          className="relative inline-flex w-full items-stretch rounded-xl"
          style={{ border: `1px solid rgba(205,214,217,0.18)` }}
        >
          <span
            className="px-3 py-2 text-sm border-r select-none whitespace-nowrap rounded-l-xl"
            style={{
              backgroundColor: C.darkBlue,
              color: C.grey1,
              borderColor: "rgba(205,214,217,0.18)",
            }}
          >
            Location
          </span>
          <select
            className="flex-1 p-2 pr-8 appearance-none rounded-r-xl"
            style={{ backgroundColor: "#020b16", color: C.offWhite }}
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            aria-label="Filter by location"
          >
            <option value="">Select location</option>
            <option value="All">All</option>
            <option value="Asia">Asia</option>
            <option value="Europe">Europe</option>
            <option value="US">US</option>
          </select>
          <ChevronDown
            size={16}
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"
            style={{ color: C.grey2 }}
          />
        </div>

        <VersionMultiSelect
          options={allVersions}
          selectedSet={versionSet}
          setSelectedSet={setVersionSet}
          allSelected={versionAllSelected}
          setAllSelected={setVersionAllSelected}
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8">
        <button
          onClick={() => setView("all")}
          className="px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
          style={brandButton({ active: view === "all" })}
          onMouseEnter={hoverNeon}
          onMouseLeave={(e) => unhoverNeon(e, view === "all")}
        >
          All
        </button>
        <button
          onClick={() => setView("favorites")}
          className="px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
          style={brandButton({ active: view === "favorites" })}
          onMouseEnter={hoverNeon}
          onMouseLeave={(e) => unhoverNeon(e, view === "favorites")}
        >
          Favorites
        </button>
      </div>

      {/* Recents */}
      {view === "all" && recentsBySearch.length > 0 && (
        <div className="mb-10">
          <div className="flex items-baseline gap-2 mb-4">
            <h2
              className="text-lg font-semibold"
              style={{ color: C.offWhite }}
            >
              Recent
            </h2>
            {filterActive && (
              <span className="text-sm" style={{ color: C.grey2 }}>
                ({recentMatching.length} match filters)
              </span>
            )}
          </div>

          {recentMatching.length > 0 && (
            <div className="flex flex-col gap-3 mb-3">
              {recentMatching.map((site) => (
                <SiteCard key={`recent-match-${site.name}`} site={site} />
              ))}
            </div>
          )}

          {filterActive && recentOther.length > 0 && (
            <div>
              <button
                onClick={() => setShowOtherRecents((v) => !v)}
                className="text-sm underline transition-colors"
                style={{ color: C.grey1 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)}
                onMouseLeave={(e) => (e.currentTarget.style.color = C.grey1)}
              >
                {showOtherRecents
                  ? "Hide"
                  : `+ ${recentOther.length} other recently viewed (outside filters)`}
              </button>

              {showOtherRecents && (
                <div className="mt-3 flex flex-col gap-3">
                  {recentOther.map((site) => (
                    <SiteCard
                      key={`recent-out-${site.name}`}
                      site={site}
                      outsideBadge
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Lists */}
      {view === "favorites" ? (
        <div>
          <h2
            className="text-lg font-semibold mb-4"
            style={{ color: C.offWhite }}
          >
            Favorites
          </h2>
          {favorites.length === 0 ? (
            <p style={{ color: C.grey2 }}>No favorites yet.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {favorites.map((site) => (
                <SiteCard key={`fav-${site.name}`} site={site} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="mb-10">
            <h2
              className="text-lg font-semibold mb-4"
              style={{ color: C.offWhite }}
            >
              Internal
            </h2>
            {renderSites(filterAndFlatten(siteData.internal))}
          </div>
          <div className="mb-10">
            <h2
              className="text-lg font-semibold mb-4"
              style={{ color: C.offWhite }}
            >
              External
            </h2>
            {renderSites(filterAndFlatten(siteData.external))}
          </div>
        </>
      )}
    </div>
  );
}